import { useState } from "react";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import { updateSaleDepositReviews } from "../../services";
import { pushGlobalSuccess } from "../../success/globalSuccess";
import { numberWithCommas } from "../../utils";

/**
 * Names the selection rather than the filter: "ทั้งหมด" over three ticked boxes of ten rows
 * would promise something the request does not do.
 */
const reviewSelectionLabel = (selectedCount: number) =>
  `ตรวจสอบ ${numberWithCommas(selectedCount)} รายการที่เลือก`;

/**
 * What the notification says the server did.
 *
 * A skip is no longer an outcome the reviewer can walk into: a row this caller cannot tick is
 * not selectable in the first place. It remains possible for the row to move between the page
 * being drawn and the request landing — the other person on this side signing it — so a skip
 * is reported as the exception it now is rather than dressed up as a clean run.
 */
const bulkReviewOutcomeMessage = (updated: number, skipped: number) =>
  skipped > 0
    ? `ตรวจสอบแล้ว ${numberWithCommas(updated)} รายการ ข้าม ${numberWithCommas(
        skipped,
      )} รายการ เพราะรายการเปลี่ยนไประหว่างบันทึก เช่น มีผู้อื่นตรวจไปก่อนแล้ว`
    : `ตรวจสอบแล้ว ${numberWithCommas(updated)} รายการ`;

interface BulkReviewActionProps {
  /** The rows the reviewer has ticked in the table's first column. */
  selectedUuids: string[];
  /**
   * Fired only after the server has accepted the write, so the caller can reload the ticks
   * and drop the selection. A refused call keeps the selection for a retry.
   */
  onCompleted: () => void | Promise<void>;
}

/**
 * Signing off the selected rows in one request.
 *
 * Head office ticks 60+ rows a day and was making one request per row. The action follows
 * the table's own selection: what will change is what the reviewer can see ticked, so there
 * is nothing to confirm before writing — the confirmation was a dialog restating a set the
 * user had never seen, which is what made it worth removing.
 *
 * The outcome goes to the app's success notification rather than an alert above the table:
 * the reviewer's eyes are on the rows they just signed, and the counts are worth a glance,
 * not a box that stays on screen until dismissed.
 */
export default function BulkReviewAction({
  selectedUuids,
  onCompleted,
}: BulkReviewActionProps) {
  const [saving, setSaving] = useState<boolean>(false);
  const selectedCount = selectedUuids.length;

  const submit = async () => {
    setSaving(true);
    try {
      const {
        data: { data },
      } = await updateSaleDepositReviews(selectedUuids, true);
      // A deployment that returns no body still accepted the write, and the request named
      // exactly these rows — the same fallback the single-row toggle makes.
      pushGlobalSuccess(
        bulkReviewOutcomeMessage(data?.updated ?? selectedCount, data?.skipped ?? 0),
      );
      await onCompleted();
    } catch {
      // The interceptor has already surfaced the failure. The selection is left standing so
      // a retry does not start by picking the same rows again.
    } finally {
      setSaving(false);
    }
  };

  if (selectedCount === 0) return null;

  return (
    <Stack alignItems="flex-end" sx={{ pb: 2 }}>
      <Button
        variant="contained"
        startIcon={<DoneAllIcon />}
        disabled={saving}
        onClick={submit}
      >
        {reviewSelectionLabel(selectedCount)}
      </Button>
    </Stack>
  );
}
